import { useState } from "react";
import type { ClientMessage, WorkspaceInfo } from "@shared/protocol";
import { useAppStore } from "../store/appStore";
import { Ico } from "./icons";

interface Props {
  send: (msg: ClientMessage) => boolean;
}

export function WorkspaceModal({ send }: Props) {
  const { workspaces, closeWorkspaceModal, setActiveWorkspace } = useAppStore();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [gitUrl, setGitUrl] = useState("");
  const [description, setDescription] = useState("");
  const [mode, setMode] = useState<"clone" | "fork" | "empty" | "github">("github");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;
    send({
      type: "workspace:create",
      payload: {
        name,
        gitUrl: gitUrl || undefined,
        mode,
        description: description || undefined,
      },
    });
    setCreating(false);
    setName(""); setGitUrl(""); setDescription("");
  };

  return (
    <div className="modal-scrim" onClick={closeWorkspaceModal}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <Ico name="folder" size={20} style={{ color: "var(--color-accent)" }} />
          <h2 className="modal-title"><em>Workspaces</em> verwalten</h2>
          <button className="icon-btn" onClick={closeWorkspaceModal} style={{ width: 28, height: 28 }}>
            <Ico name="x" size={16} />
          </button>
        </header>

        <div className="modal-body">
          <div style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr 1fr auto",
            gap: "1rem",
            padding: "0 0.75rem var(--space-2)",
            fontSize: 10.5,
            color: "var(--color-fg-subtle)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            borderBottom: "1px solid var(--color-line)",
          }}>
            <span>Projekt</span>
            <span>Branch</span>
            <span>Pfad</span>
            <span style={{ textAlign: "right" }}>Aktion</span>
          </div>

          {workspaces.map((p) => (
            <WorkspaceRow key={p.id} ws={p} send={send} onOpen={() => { setActiveWorkspace(p.id); closeWorkspaceModal(); }} />
          ))}

          {creating ? (
            <form onSubmit={submit} className="ws-new" style={{ flexDirection: "column", alignItems: "stretch" }}>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", width: "100%" }}>
                <input
                  className="input" placeholder="projekt-name" value={name}
                  onChange={(e) => setName(e.target.value)}
                  title="nur Buchstaben, Zahlen, _ . - (kein Leerzeichen, kein Umlaut)"
                  style={{ flex: 1 }}
                />
                <select className="input" value={mode}
                        onChange={(e) => setMode(e.target.value as "clone" | "fork" | "empty" | "github")}>
                  <option value="github">GitHub: neu anlegen</option>
                  <option value="fork">fork (existierender bare repo)</option>
                  <option value="clone">git clone (externe URL)</option>
                  <option value="empty">empty (lokal, kein Remote)</option>
                </select>
              </div>
              {mode === "github" && (
                <input
                  className="input"
                  placeholder="kurze Beschreibung (optional, landet als GitHub repo description)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              )}
              {mode === "clone" && (
                <input className="input" placeholder="git url (https oder ssh)" value={gitUrl}
                       onChange={(e) => setGitUrl(e.target.value)} />
              )}
              <div style={{ fontSize: 11, color: "var(--color-fg-subtle)", paddingLeft: 2 }}>
                {mode === "github" && "Legt das Repo privat in matrokit an, bare-clont nach /srv/repos und forkt zu dir."}
                {mode === "fork" && "Klont aus /srv/repos/<name>.git in dein ~/projects/."}
                {mode === "clone" && "Klont von externer URL in dein ~/projects/."}
                {mode === "empty" && "git init in ~/projects/<name>. Kein Remote."}
              </div>
              <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                <button type="button" className="btn btn-ghost" onClick={() => setCreating(false)}>Abbrechen</button>
                <button type="submit" className="btn btn-primary">
                  {mode === "github" ? "GitHub anlegen + forken" : "Anlegen"}
                </button>
              </div>
            </form>
          ) : (
            <button className="ws-new" onClick={() => setCreating(true)}>
              <Ico name="plus" size={16} />
              <div>
                <div style={{ color: "var(--color-fg)", fontWeight: 480 }}>Neuen Workspace anlegen</div>
                <div style={{ fontSize: 11, color: "var(--color-fg-subtle)", marginTop: 2 }}>
                  Neu auf GitHub anlegen, bestehenden bare repo forken, externe URL clonen, oder leer
                </div>
              </div>
              <div className="opts">
                <span>github</span><span>fork</span><span>clone</span><span>empty</span>
              </div>
            </button>
          )}
        </div>

        <footer className="modal-foot">
          <span style={{ fontSize: 12, color: "var(--color-fg-subtle)" }}>
            {workspaces.length} Workspace{workspaces.length !== 1 ? "s" : ""}
          </span>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button className="btn btn-ghost" onClick={closeWorkspaceModal}>Schließen</button>
            <button
              className="btn btn-primary"
              onClick={() => workspaces.forEach((w) => send({ type: "git:pull", payload: { workspacePath: w.path } }))}
            >
              Alle pullen
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function WorkspaceRow({ ws, send, onOpen }: { ws: WorkspaceInfo; send: (m: ClientMessage) => boolean; onOpen: () => void }) {
  return (
    <div className="ws-row">
      <div className="ws-name">
        <span className="dot" style={{ background: ws.dot }} />
        <div>
          <div className="title">{ws.name}</div>
          <div className="path">{ws.path}</div>
        </div>
      </div>
      <div className="branch-cell">
        <span className="who">Branch</span>
        <div className="row">
          <Ico name="branch" size={11} style={{ color: "var(--color-fg-subtle)", flexShrink: 0 }} />
          <span className="branch-name">{ws.branch}</span>
        </div>
      </div>
      <div className="branch-cell">
        <span className="who">Sessions</span>
        <div className="row">
          <span className="branch-name">{ws.agents} aktiv</span>
        </div>
      </div>
      <div className="actions">
        <button title="git pull" onClick={() => send({ type: "git:pull", payload: { workspacePath: ws.path } })}>
          <Ico name="pull" size={11} />
        </button>
        <button onClick={() => send({ type: "git:status", payload: { workspacePath: ws.path } })}>status</button>
        <button className="primary" onClick={onOpen}>öffnen</button>
      </div>
    </div>
  );
}
